import type { AuthClientConfig, IdentityPlatformWebConfig } from "./types.js";

export class IdentityUnavailableError extends Error {
  public constructor() {
    super("Identity Platform is not configured for this environment.");
    this.name = "IdentityUnavailableError";
  }
}

export class IdentitySignInCancelledError extends Error {
  public constructor() {
    super("Sign-in was closed before it completed.");
    this.name = "IdentitySignInCancelledError";
  }
}

export interface IdentityCredential {
  readonly idToken: string;
  readonly email: string | undefined;
  readonly displayName: string | undefined;
}

export interface IdentityClient {
  /** Completes Google sign-in and returns the provider credential for server-side exchange. */
  signInWithGoogle(): Promise<IdentityCredential>;
  /** Recovers a credential left by an older redirect-based sign-in, when present. */
  completeGoogleSignIn(): Promise<IdentityCredential | undefined>;
  /** Removes a just-created provider identity after the server rejects private-beta access. */
  deleteCurrentUser(): Promise<void>;
  signOut(): Promise<void>;
}

/** Existing-member authentication is independent of the self-serve organization policy. */
export function hasIdentityPlatformEntry(
  config: AuthClientConfig,
): config is AuthClientConfig & { readonly identityPlatform: IdentityPlatformWebConfig } {
  return config.identityPlatform !== undefined;
}

/**
 * Loads the Identity Platform client on demand. The SDK is only needed by a visitor who has no
 * session yet, so keeping it out of the static graph means signed-in users never download it.
 */
export async function loadIdentityClient(
  config: IdentityPlatformWebConfig,
): Promise<IdentityClient> {
  const { FirebaseIdentityClient } = await import("./identity-firebase.js");
  return new FirebaseIdentityClient(config);
}
